import { z } from 'zod'
import { create } from 'zustand'
import { isPauseDateWithinLimit } from '@/lib/tracking-pause'
import type { UserSession } from '@/lib/types'
import { isValidTimeRange } from '@/lib/working-hours'
import { DEFAULT_WORKING_HOURS } from '@/types/workingHours'

const TIME_REGEX = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/

export const myDetailsSchema = z
    .object({
        name: z.string().min(1, 'Name is required'),
        email: z.string().email('Please enter a valid email address'),
        geo_tracking_disabled: z.boolean(),
        working_hours_override: z.boolean(),
        work_start_time: z.string().regex(TIME_REGEX),
        work_end_time: z.string().regex(TIME_REGEX),
        working_days: z.array(z.number().min(0).max(6)),
        tracking_paused_until: z.string().nullable().optional(),
    })
    .refine(
        data => {
            if (!data.working_hours_override) return true
            return isValidTimeRange(data.work_start_time, data.work_end_time)
        },
        {
            message: 'Start time must be before end time',
            path: ['work_end_time'],
        }
    )
    .refine(data => isPauseDateWithinLimit(data.tracking_paused_until), {
        message: 'Tracking can only be paused for up to one month',
        path: ['tracking_paused_until'],
    })

export type MyDetailsFormData = z.infer<typeof myDetailsSchema>

export interface UserOrgWorkingHoursData {
    workingHoursOverride: boolean
    workStartTime: string | null
    workEndTime: string | null
    workingDays: number[] | null
    trackingPausedUntil: string | null
}

export function getDefaultFormValues(
    user: UserSession,
    userOrgData?: UserOrgWorkingHoursData
): MyDetailsFormData {
    return {
        name: user.name || '',
        email: user.email || '',
        geo_tracking_disabled: user.geo_tracking_disabled ?? false,
        working_hours_override: userOrgData?.workingHoursOverride || false,
        work_start_time: userOrgData?.workStartTime || DEFAULT_WORKING_HOURS.startTime,
        work_end_time: userOrgData?.workEndTime || DEFAULT_WORKING_HOURS.endTime,
        working_days: userOrgData?.workingDays || DEFAULT_WORKING_HOURS.workingDays,
        tracking_paused_until: userOrgData?.trackingPausedUntil || null,
    }
}

export const changePasswordSchema = z
    .object({
        oldPassword: z.string().min(1, 'Current password is required'),
        password: z.string().min(8, 'Password must be at least 8 characters'),
        passwordConfirm: z.string().min(1, 'Please confirm your password'),
    })
    .refine(data => data.password === data.passwordConfirm, {
        message: 'Passwords do not match',
        path: ['passwordConfirm'],
    })

export type ChangePasswordFormData = z.infer<typeof changePasswordSchema>

interface MyDetailsStore {
    showDeleteDialog: boolean
    setShowDeleteDialog: (show: boolean) => void
    showPasswordDialog: boolean
    setShowPasswordDialog: (show: boolean) => void
}

export const useMyDetailsStore = create<MyDetailsStore>()(set => ({
    showDeleteDialog: false,
    setShowDeleteDialog: show => set({ showDeleteDialog: show }),
    showPasswordDialog: false,
    setShowPasswordDialog: show => set({ showPasswordDialog: show }),
}))

export const useShowDeleteDialog = () => useMyDetailsStore(state => state.showDeleteDialog)
export const useSetShowDeleteDialog = () => useMyDetailsStore(state => state.setShowDeleteDialog)
export const useShowPasswordDialog = () => useMyDetailsStore(state => state.showPasswordDialog)
export const useSetShowPasswordDialog = () =>
    useMyDetailsStore(state => state.setShowPasswordDialog)

export function computeChanges(
    formValues: MyDetailsFormData,
    user: UserSession,
    userOrgData?: UserOrgWorkingHoursData
) {
    const hasProfileChanges = formValues.name !== user.name || formValues.email !== user.email
    const hasGeoTrackingChanges =
        formValues.geo_tracking_disabled !== (user.geo_tracking_disabled ?? false)
    const hasWorkingHoursChanges =
        formValues.working_hours_override !== (userOrgData?.workingHoursOverride || false) ||
        formValues.work_start_time !==
            (userOrgData?.workStartTime || DEFAULT_WORKING_HOURS.startTime) ||
        formValues.work_end_time !== (userOrgData?.workEndTime || DEFAULT_WORKING_HOURS.endTime) ||
        JSON.stringify(formValues.working_days) !==
            JSON.stringify(userOrgData?.workingDays || DEFAULT_WORKING_HOURS.workingDays)
    const hasTrackingPauseChanges =
        (formValues.tracking_paused_until || null) !== (userOrgData?.trackingPausedUntil || null)

    return {
        hasProfileChanges,
        hasGeoTrackingChanges,
        hasWorkingHoursChanges,
        hasTrackingPauseChanges,
        hasSettingsChanges:
            hasProfileChanges ||
            hasGeoTrackingChanges ||
            hasWorkingHoursChanges ||
            hasTrackingPauseChanges,
    }
}

export function getSuccessMessage(changes: ReturnType<typeof computeChanges>): string {
    if (changes.hasSettingsChanges) {
        return 'Settings updated successfully'
    }
    return ''
}

interface UserUpdateData {
    name?: string
    email?: string
    geo_tracking_disabled?: boolean
}

interface UserOrgUpdateData {
    working_hours_override?: boolean
    work_start_time?: string
    work_end_time?: string
    working_days?: number[] | null
    tracking_paused_until?: string | null
}

export function buildUserUpdateData(
    data: MyDetailsFormData,
    user: UserSession,
    changes: ReturnType<typeof computeChanges>
): UserUpdateData {
    const updateData: UserUpdateData = {}

    if (data.name !== user.name) updateData.name = data.name
    if (data.email !== user.email) updateData.email = data.email

    if (changes.hasGeoTrackingChanges) {
        updateData.geo_tracking_disabled = data.geo_tracking_disabled
    }

    return updateData
}

export function buildUserOrgUpdateData(
    data: MyDetailsFormData,
    changes: ReturnType<typeof computeChanges>
): UserOrgUpdateData {
    const updateData: UserOrgUpdateData = {}

    if (changes.hasWorkingHoursChanges) {
        updateData.working_hours_override = data.working_hours_override
        updateData.work_start_time = data.working_hours_override ? data.work_start_time : ''
        updateData.work_end_time = data.working_hours_override ? data.work_end_time : ''
        updateData.working_days = data.working_hours_override ? data.working_days : null
    }

    if (changes.hasTrackingPauseChanges) {
        updateData.tracking_paused_until = data.tracking_paused_until || ''
    }

    return updateData
}
